import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Copy, Check, Link2 } from "lucide-react";
import { toast } from "sonner";

interface AddChildFormProps {
  householdId: string;
  onInviteSent: () => void;
  disabled?: boolean;
  slotsRemaining?: number;
}

const buildInviteLink = (code: string) =>
  `https://heirwise.app/login?invite=${encodeURIComponent(code)}`;

const AddChildForm = ({ householdId, onInviteSent, disabled = false, slotsRemaining }: AddChildFormProps) => {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastCode, setLastCode] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<"code" | "link" | null>(null);
  const hasReachedLimit = disabled || (slotsRemaining ?? 1) <= 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !displayName.trim() || submitting || hasReachedLimit) return;

    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc("create_household_invite", {
        p_household_id: householdId,
        p_email: email.trim().toLowerCase(),
        p_display_name: displayName.trim(),
      });

      if (error) throw error;

      const invite = data as { invite_code?: string } | null;
      setLastCode(invite?.invite_code ?? null);
      setEmail("");
      setDisplayName("");
      onInviteSent();
      toast.success("Family invite created. Share the code or link with your child.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create invite.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyValue = async (value: string, field: "code" | "link") => {
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    toast.success(field === "link" ? "Invite link copied." : "Invite code copied.");
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="space-y-4 overflow-x-hidden">
      <div className="rounded-xl border border-border bg-muted/20 p-3 space-y-2">
        <p className="text-xs leading-relaxed text-muted-foreground">
          Add a child name and email. We'll generate a code and a one-tap invite link. Your child clicks the link (or selects <strong>Join Family</strong> on the signup page and enters the code) to join your household — no separate payment.
        </p>
        <p className="text-xs font-medium text-foreground">
          {hasReachedLimit
            ? "4 of 4 child slots are filled or reserved. Remove a pending invite or child profile to add another."
            : `${slotsRemaining ?? 0} child slot${slotsRemaining === 1 ? "" : "s"} remaining.`}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="member-name" className="text-xs text-muted-foreground">Member Name</Label>
            <Input
              id="member-name"
              name="member-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Alex"
              required
              disabled={hasReachedLimit}
              className="h-10 text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="member-email" className="text-xs text-muted-foreground">Member Email</Label>
            <Input
              id="member-email"
              name="member-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alex@email.com"
              required
              disabled={hasReachedLimit}
              className="h-10 text-sm"
            />
          </div>
        </div>

        <div className="rounded-xl border border-border/60 bg-card/40 p-3">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Role</p>
          <p className="mt-1 text-sm text-foreground">Child account</p>
          <p className="text-xs text-muted-foreground">Covered by your household subscription. Invite expires in 14 days.</p>
        </div>

        <Button type="submit" disabled={submitting || !email.trim() || !displayName.trim() || hasReachedLimit} className="w-full gap-2">
          <UserPlus className="h-4 w-4" />
          {hasReachedLimit ? "Child Limit Reached" : submitting ? "Creating invite..." : "Add Family Member"}
        </Button>
      </form>

      {lastCode && (
        <div className="space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-3 overflow-hidden">
          <p className="text-xs font-medium text-foreground">Share either of these with your child</p>

          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Invite Code</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <code className="flex-1 overflow-x-auto rounded-lg border border-border bg-background/70 px-3 py-2 text-center font-mono text-base tracking-[0.3em] text-foreground sm:text-lg">
                {lastCode}
              </code>
              <Button variant="outline" onClick={() => void copyValue(lastCode, "code")} className="gap-2 sm:w-auto">
                {copiedField === "code" ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                {copiedField === "code" ? "Copied" : "Copy code"}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">One-tap Invite Link</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <code className="flex-1 overflow-x-auto rounded-lg border border-border bg-background/70 px-3 py-2 text-xs text-foreground">
                {buildInviteLink(lastCode)}
              </code>
              <Button variant="outline" onClick={() => void copyValue(buildInviteLink(lastCode), "link")} className="gap-2 sm:w-auto">
                {copiedField === "link" ? <Check className="h-4 w-4 text-primary" /> : <Link2 className="h-4 w-4" />}
                {copiedField === "link" ? "Copied" : "Copy link"}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              The link opens our signup page with the code already attached.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddChildForm;
